<script setup lang="ts">
import { computed, useSlots } from 'vue';

import { ItinerarySegmentType } from '../../models';

interface ItineraryTimelineProps {
  segmentType: ItinerarySegmentType;
}

const props = defineProps<ItineraryTimelineProps>();

const slots = useSlots();
const shouldHaveGap = computed<boolean>(
  () => (!!slots['header'] || !!slots['dropdown']) && !!slots['content']
);
const isDropdown = computed<boolean>(() => !!slots['dropdown']);
</script>

<template>
  <div
    class="itinerary-timeline"
    :class="[
      {
        'end-location': props.segmentType === ItinerarySegmentType.EndLocation,
        'row-gap': shouldHaveGap,
        upsell: props.segmentType === ItinerarySegmentType.Upsell,
        'alternative-grid': isDropdown,
      },
    ]"
  >
    <div v-if="isDropdown" class="first-row">
      <div class="itinerary-icon">
        <slot name="icon" />
      </div>
      <div class="itinerary-dropdown">
        <slot name="dropdown" />
      </div>
      <div class="header">
        <slot name="header" />
      </div>
      <div class="large-header">
        <slot name="large-header" />
      </div>
    </div>
    <div v-if="!isDropdown" class="itinerary-icon">
      <slot name="icon" />
    </div>
    <div v-if="!isDropdown" class="itinerary-dropdown">
      <slot name="dropdown" />
    </div>
    <div v-if="!isDropdown" class="header">
      <slot name="header" />
    </div>
    <div class="content">
      <slot name="content" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.itinerary-timeline {
  --left-spacing: var(--spacing-32);
  --column-gap-spacing: var(--spacing-24);

  padding: 0 var(--spacing-24) var(--spacing-24) var(--spacing-16);

  display: grid;
  grid-template-rows: auto auto;
  grid-template-columns: var(--left-spacing) auto;
  grid-column-gap: var(--column-gap-spacing);

  position: relative;

  &.alternative-grid {
    display: flex;
    flex-direction: column;
    .first-row {
      display: grid;
      grid-template-columns: auto 1fr;
      align-items: center;
      justify-content: start;
      @include for-less-or-equal-than-599-px {
        grid-template-rows: auto auto;

        .itinerary-dropdown {
          grid-row: 1/2;
          grid-column: 1/2;
        }

        .header {
          grid-row: 1/-1;
          grid-column: 2/3;
        }
      }
    }
    .content {
      margin-left: calc(var(--left-spacing) + var(--column-gap-spacing));
      @include for-less-or-equal-than-599-px {
        margin-left: 0;
      }
    }
  }

  &::after {
    content: '';
    height: 100%;
    width: 0px;
    border-right: 1px dashed var(--color-neutral-70);
    position: absolute;
    top: 0;
    left: 49px;

    @include for-less-or-equal-than-599-px {
      left: 37px;
    }
  }

  // last location in timeline can't extend to next segment
  &.end-location::after {
    height: 0;
  }

  &.row-gap {
    grid-row-gap: var(--spacing-16);
  }

  &.upsell {
    .content {
      overflow-x: auto;
    }
  }

  .itinerary-icon {
    grid-column: 1/2;
    grid-row: 1/2;
    z-index: 2;
    justify-self: center;
    align-self: start;
    margin-left: 36px;

    @include for-less-or-equal-than-599-px {
      margin-left: 20px;
    }
  }
  .itinerary-dropdown {
    grid-column: 1/2;
    grid-row: 1/2;
    z-index: 3;
    align-self: start;
    margin-left: var(--spacing-12);

    @include for-less-or-equal-than-599-px {
      margin-left: 0px;
    }
  }

  .header {
    grid-column: 2/3;
    grid-row: 1/2;
    align-self: center;
    display: flex;
    margin-left: 1px;
    min-width: 0;

    @include for-less-or-equal-than-599-px {
      margin-left: -6px;
    }
  }

  .large-header {
    grid-column: 1/3;
    grid-row: 1/2;
  }

  .content {
    grid-column: 2/3;
    grid-row: 2/3;
    z-index: 1;
    min-width: 0;

    @include for-less-or-equal-than-599-px {
      grid-column: 1/3;
    }
  }

  @include for-less-or-equal-than-599-px {
    padding: 0 var(--spacing-12) var(--spacing-24) var(--spacing-12);
  }
}
</style>
